import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Container, Header, ListaComentarios, SectionHeader, ButtonNovoComentario } from "./styles";
import { ContainerBloco } from "../Common/styles";
import { MessageSquare, Plus } from "lucide-react";
import { comum } from "../../../styles/Themes/light";
import comentarioService, { IComentario } from "../../../services/comentarioService";
import CardComentario from "./CardComentario";
import FormNovoComentario from "./FormNovoComentario";
import ComentariosSkeleton from "./ComentariosSkeleton";
import AbaErro from "../Common/AbaErro";
import AbaAcessoNegado from "../Common/AbaAcessoNegado";
import { isAcessoNegado } from "../Common/utils";
import EmptyState from "./EmptyState";
import toast from "react-hot-toast";
import { toastDefsError } from "../../../config/toastConfig";
import { useAuth } from "../../../contexts/UserContext";

interface AbaComentariosProps {
    abaAtiva: string;
    id_proposta: number;
    demanda?: any;
    acessoNegado?: boolean;
    onContadorAtualizado?: (contador: number) => void;
}

export default function AbaComentarios({ 
    abaAtiva, 
    id_proposta, 
    demanda,
    acessoNegado,
    onContadorAtualizado 
}: AbaComentariosProps) {
    const [comentarios, setComentarios] = useState<IComentario[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<boolean | string>(false);
    const [contador, setContador] = useState(0);
    const [isRetrying, setIsRetrying] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [comentarioRespondendo, setComentarioRespondendo] = useState<number | null>(null);
    const [idComentarioPaiParaResposta, setIdComentarioPaiParaResposta] = useState<number | null>(null);
    const listaComentariosRef = useRef<HTMLDivElement>(null);
    const { user } = useAuth();
    const location = useLocation();
    const history = useHistory();

    const carregarComentarios = useCallback(async (isRetry = false) => {
        if (!id_proposta) return;

        if (isRetry) {
            setIsRetrying(true);
        } else {
            setLoading(true);
        }
        setError(false);
        try {
            const [comentariosData, contadorData] = await Promise.all([
                comentarioService.getComentarios(id_proposta),
                comentarioService.getContador(id_proposta)
            ]);
            
            setComentarios(comentariosData);
            setContador(contadorData);
            onContadorAtualizado?.(contadorData);
        } catch (error: any) {
            console.error('Erro ao carregar comentários:', error);
            if (isAcessoNegado(error)) {
                setError('acesso_negado');
            } else {
                setError(true);
                toast.error('Erro ao carregar comentários', toastDefsError);
            }
        } finally {
            setLoading(false);
            setIsRetrying(false);
        }
    }, [id_proposta, onContadorAtualizado]);

    const scrollToComentario = useCallback((id_comentario: number) => {
        if (!listaComentariosRef.current) return;

        const elemento = listaComentariosRef.current.querySelector(`[data-comentario-id="${id_comentario}"]`) as HTMLElement;
        
        if (elemento) {
            elemento.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            elemento.style.transition = 'all 0.3s ease';
            elemento.style.border = '2px solid rgba(229, 41, 64, 0.5)';
            elemento.style.borderRadius = '8px';
            elemento.style.backgroundColor = 'rgba(229, 41, 64, 0.1)';
            elemento.style.padding = '8px';
            elemento.style.margin = '-8px';
            
            setTimeout(() => {
                elemento.style.border = '';
                elemento.style.backgroundColor = '';
                elemento.style.padding = '';
                elemento.style.margin = '';
                setTimeout(() => {
                    elemento.style.transition = '';
                }, 300);
            }, 3000);
        }
    }, []);

    useEffect(() => {
        if (abaAtiva === 'comentarios' && id_proposta) {
            carregarComentarios();
        }
    }, [abaAtiva, id_proposta, carregarComentarios]);

    useEffect(() => {
        if (abaAtiva === 'comentarios' && !loading && comentarios.length > 0) {
            const highlightId = sessionStorage.getItem('highlightComentario');
            
            if (highlightId) {
                const id = Number(highlightId);
                if (id) {
                    sessionStorage.removeItem('highlightComentario');
                    
                    setTimeout(() => {
                        scrollToComentario(id);
                    }, 300);
                }
            }
        }
    }, [abaAtiva, loading, comentarios, scrollToComentario]);

    const handleComentarioCriado = async () => {
        setComentarioRespondendo(null);
        setIdComentarioPaiParaResposta(null);
        await carregarComentarios();
    };

    const encontrarComentarioPrincipalRecursivo = useCallback((id_comentario: number, respostas: IComentario[]): boolean => {
        for (const resposta of respostas) {
            if (resposta.id_comentario === id_comentario) {
                return true;
            }
            if (resposta.respostas && resposta.respostas.length > 0) {
                if (encontrarComentarioPrincipalRecursivo(id_comentario, resposta.respostas)) {
                    return true;
                }
            }
        }
        return false;
    }, []);

    const encontrarComentarioPrincipal = useCallback((id_comentario: number, comentarios: IComentario[]): number | null => {
        for (const comentario of comentarios) {
            if (comentario.id_comentario === id_comentario) {
                return comentario.id_comentario;
            }
            if (comentario.respostas && comentario.respostas.length > 0) {
                const encontrado = encontrarComentarioPrincipalRecursivo(id_comentario, comentario.respostas);
                if (encontrado) {
                    return comentario.id_comentario;
                }
            }
        }
        return null;
    }, [encontrarComentarioPrincipalRecursivo]);

    const handleIniciarResposta = useCallback((id_comentario: number) => {
        const idComentarioPrincipal = encontrarComentarioPrincipal(id_comentario, comentarios);
        setIdComentarioPaiParaResposta(id_comentario);
        
        if (idComentarioPrincipal) {
            setComentarioRespondendo(idComentarioPrincipal);
            setTimeout(() => {
                const formElement = document.querySelector(`[data-form-resposta="${idComentarioPrincipal}"]`) as HTMLElement;
                if (formElement) {
                    formElement.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center',
                        inline: 'nearest'
                    });
                }
            }, 200);
        } else {
            setComentarioRespondendo(id_comentario);
            setTimeout(() => {
                const formElement = document.querySelector(`[data-form-resposta="${id_comentario}"]`) as HTMLElement;
                if (formElement) {
                    formElement.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center',
                        inline: 'nearest'
                    });
                }
            }, 200);
        }
    }, [comentarios, encontrarComentarioPrincipal]);

    const handleCancelarResposta = useCallback(() => {
        setComentarioRespondendo(null);
        setIdComentarioPaiParaResposta(null);
    }, []);

    const getComentariosVisiveis = (comentarios: IComentario[]): IComentario[] => {
        return comentarios;
    };

    const handleOpenForm = () => {
        setIsFormOpen(true);
    };

    if (abaAtiva !== 'comentarios') {
        return null;
    }

    if (acessoNegado) {
        return <AbaAcessoNegado />;
    }

    const userData = user ? {
        tipo: user.profile === 'admin' ? 'Admin' : user.profile === 'operador' ? 'Operador' : user.profile === 'comum' ? 'Profissional' : 'Solicitante',
        id: user.id,
        id_operador: user.profile === 'admin' || user.profile === 'operador' ? Number(user.id) : undefined,
        id_usuario: user.profile === 'comum' ? Number(user.id) : undefined,
        id_solicitante: user.profile === 'Solicitante' ? Number(user.id) : undefined,
        id_escritorio_solicitante: user.id_escritorio_solicitante
    } : {
        tipo: ''
    };

    return (
        <Container>
            <ContainerBloco>
                <Header>
                    <div className="titulo">
                        <MessageSquare size={18} color={comum.colors.primaryRed400} aria-hidden="true" />
                        <div>Comentários</div>
                    </div>
                    {!loading && !error && error !== 'acesso_negado' && !acessoNegado && userData.tipo && (
                        <ButtonNovoComentario onClick={handleOpenForm} aria-label="Abrir formulário para criar novo comentário">
                            <Plus size={16} aria-hidden="true" />
                            Novo Comentário
                        </ButtonNovoComentario>
                    )}
                </Header>

                {loading ? (
                    <ComentariosSkeleton count={3} showCounter={true} />
                ) : error === 'acesso_negado' || acessoNegado ? (
                    <AbaAcessoNegado />
                ) : error ? (
                    <AbaErro 
                        mensagem="Não foi possível carregar comentários."
                        onRetry={() => carregarComentarios(true)}
                    />
                ) : (
                    <>
                        {isFormOpen && (
                            <FormNovoComentario
                                id_proposta={id_proposta}
                                onComentarioCriado={() => {
                                    handleComentarioCriado();
                                    setIsFormOpen(false);
                                }}
                                user={userData}
                                proposta={demanda}
                                onOpenChange={setIsFormOpen}
                                forceOpen={true}
                            />
                        )}

                        <SectionHeader>
                            <h3>COMENTÁRIOS PUBLICADOS ({contador})</h3>
                        </SectionHeader>
                        {comentarios.length === 0 ? (
                            <EmptyState message="Nenhum comentário disponível" />
                        ) : (
                            <ListaComentarios ref={listaComentariosRef} role="list" aria-label="Lista de comentários">
                                {getComentariosVisiveis(comentarios).map((comentario) => (
                                    <div key={comentario.id_comentario} data-comentario-id={comentario.id_comentario}>
                                        <CardComentario 
                                            comentario={comentario}
                                            nivel={0}
                                            id_proposta={id_proposta}
                                            onRespostaCriada={handleComentarioCriado}
                                            onComentarioAtualizado={handleComentarioCriado}
                                            onIniciarResposta={handleIniciarResposta}
                                            comentarioRespondendo={comentarioRespondendo}
                                            idComentarioPaiParaResposta={idComentarioPaiParaResposta}
                                            onCancelarResposta={handleCancelarResposta}
                                            todosComentarios={comentarios}
                                            user={userData}
                                            demanda={demanda}
                                        />
                                    </div>
                                ))}
                            </ListaComentarios>
                        )}
                    </>
                )}
            </ContainerBloco>
        </Container>
    );
}
